<template>
  <div>
    <v-card variant="outlined" class="mb-2" :class="statusClass">
      <v-card-title class="d-flex align-center py-2 px-4">
        <v-icon :icon="statusIcon" :color="statusColor" size="small" class="mr-2" />
        <span class="text-subtitle-2">{{ collapsedTitle }}</span>
        <v-chip v-if="stepRun.status !== 'queued'" size="x-small" density="compact" :color="statusColor" class="ml-2">
          {{ stepRun.status }}
        </v-chip>
        <v-chip size="x-small" density="compact" class="ml-2" variant="tonal">
          {{ stepRun.invoked.kind }}
        </v-chip>
        <v-spacer />
        <span v-if="stepRun.usage" class="text-caption text-medium-emphasis">
          {{ stepRun.usage.promptTokens || 0 }} prompt |
          {{ stepRun.usage.completionTokens || 0 }} completion |
          {{ stepRun.usage.totalTokens || 0 }} total
        </span>
        <v-btn size="x-small" density="compact" variant="text" :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="expanded = !expanded" />
      </v-card-title>
      <v-expand-transition>
        <div v-if="expanded">
          <v-divider />
          <v-card-text class="pa-2">
            <div class="text-caption text-medium-emphasis mb-2">
              Sequence {{ stepRun.sequence }} · Triggered by {{ stepRun.triggeredBy }}
            </div>

            <div v-if="stepRun.input" class="mb-3">
              <div class="text-overline mb-1">Input</div>
              <pre class="factory-step-pre">{{ stepRun.input }}</pre>
            </div>

            <ToolEventsPanel
              v-if="stepRun.toolEvents.length > 0"
              :tool-events="stepRun.toolEvents"
              :show-activity="stepRun.status === 'running'"
              compact
            />

            <FormattingCard
              v-if="stepRun.output"
              :content="stepRun.output"
              :expanded="'true'"
              :title="null"
              :streaming="stepRun.status === 'running'"
            />

            <div v-if="stepRun.validation" class="mt-3">
              <div class="text-overline mb-1">Validation</div>
              <v-chip size="x-small" density="compact" class="mr-2" :color="stepRun.validation.finalOutcome === 'pass' ? 'success' : 'error'">
                {{ stepRun.validation.finalOutcome }}
              </v-chip>
              <span class="text-caption">
                initial {{ stepRun.validation.initialOutcome }} · repairs {{ stepRun.validation.repairAttempts }}
              </span>
              <ul v-if="stepRun.validation.errors?.length" class="mt-2 factory-step-errors">
                <li v-for="error in stepRun.validation.errors" :key="`${error.path}-${error.message}`">
                  {{ error.path || '(root)' }}: {{ error.message }}
                </li>
              </ul>
            </div>

            <div class="d-flex justify-end mt-3 ga-2">
              <v-btn
                v-if="stepRun.workflowRunId && stepRun.invoked.workflowConfigId"
                size="small"
                density="compact"
                variant="text"
                @click="$emit('open-workflow', stepRun)"
              >
                Open workflow run
              </v-btn>
              <v-btn
                v-if="stepRun.chatId"
                size="small"
                density="compact"
                variant="text"
                @click="$emit('open-chat', stepRun)"
              >
                Open chat
              </v-btn>
            </div>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <div v-if="stepRun.children?.length" class="factory-step-children">
      <FactoryStepRunCard
        v-for="child in stepRun.children"
        :key="child.stepRunId"
        :step-run="child"
        @open-workflow="$emit('open-workflow', $event)"
        @open-chat="$emit('open-chat', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import FormattingCard from '@/components/cards/FormattingCard.vue'
import ToolEventsPanel from '@/components/sub/ToolEventsPanel.vue'
import type { FactoryStepRunDisplay, FactoryStepRunRecord } from '@/types/factories'

defineEmits<{
  (e: 'open-workflow', stepRun: FactoryStepRunRecord): void
  (e: 'open-chat', stepRun: FactoryStepRunRecord): void
}>()

const props = defineProps<{
  stepRun: FactoryStepRunDisplay
}>()

const expanded = ref(props.stepRun.status === 'running')

const collapsedTitle = computed(() => {
  const ordinal = String(props.stepRun.stepIndex + 1)
  const invokedName = props.stepRun.invoked.agentName || props.stepRun.invoked.workflowName
  if (invokedName && invokedName.trim()) {
    return `${ordinal} ${invokedName}`.trim()
  }

  const genericStepName = new RegExp(`^step\\s+${ordinal}$`, 'i')
  if (genericStepName.test(props.stepRun.stepName.trim())) {
    return ordinal
  }

  return `${ordinal} ${props.stepRun.stepName}`.trim()
})

const statusIcon = computed(() => {
  switch (props.stepRun.status) {
    case 'queued': return 'mdi-clock-outline'
    case 'running': return 'mdi-loading mdi-spin'
    case 'completed': return 'mdi-check-circle'
    case 'cancelled': return 'mdi-cancel'
    case 'error': return 'mdi-alert-circle'
    default: return 'mdi-circle-outline'
  }
})

const statusColor = computed(() => {
  switch (props.stepRun.status) {
    case 'queued': return 'grey'
    case 'running': return 'primary'
    case 'completed': return 'success'
    case 'cancelled': return 'warning'
    case 'error': return 'error'
    default: return 'grey'
  }
})

const statusClass = computed(() => {
  if (props.stepRun.status === 'running') return 'border-primary'
  if (props.stepRun.status === 'cancelled') return 'border-warning'
  if (props.stepRun.status === 'error') return 'border-error'
  return ''
})
</script>

<style scoped>
.border-primary {
  border-color: rgb(var(--v-theme-primary)) !important;
}
.border-warning {
  border-color: rgb(var(--v-theme-warning)) !important;
}
.border-error {
  border-color: rgb(var(--v-theme-error)) !important;
}
.factory-step-pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(var(--v-theme-surface-variant), 0.35);
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
}
.factory-step-errors {
  padding-left: 18px;
}
.factory-step-children {
  margin-left: 20px;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.18);
  padding-left: 12px;
}
</style>
